import { useState, useRef, useEffect } from "react";
import {
  Input,
  Box,
  VStack,
  Text,
  useDisclosure,
  useOutsideClick,
  Icon,
  InputGroup,
  InputRightElement,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon, CloseIcon } from "@chakra-ui/icons";

interface SearchableSelectProps {
  value: string | number | null;
  onChange: (value: string | null) => void;
  placeholder: string;
  isDisabled?: boolean;
  options: any[];
  valueKey?: string;
  searchKeys?: string[];
  getDisplayText?: (option: any) => string;
  getSecondaryText?: (option: any) => string | null;
  noOptionsText?: string;
  placeholderColor?: string;
  maxHeight?: string;
  displayFormat?: (option: any) => string;
}

export const SearchableSelect = ({
  value,
  onChange,
  placeholder,
  isDisabled = false,
  options = [],
  valueKey = "id",
  searchKeys = [],
  getDisplayText,
  getSecondaryText,
  noOptionsText = "No options found",
  placeholderColor = "black",
  maxHeight = "200px",
  displayFormat,
}: SearchableSelectProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const { isOpen, onToggle, onClose } = useDisclosure();
  const ref = useRef();

  useOutsideClick({
    ref: ref,
    handler: onClose,
  });

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter((option) =>
        searchKeys.some(
          (key) =>
            option[key] &&
            option[key].toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options, searchKeys]);

  const selectedOption = options.find((option) => option[valueKey] === value);

  const handleSelect = (option: any) => {
    onChange(option[valueKey]);
    onClose();
    setSearchTerm("");
  };

  const handleClear = () => {
    onChange(null);
    setSearchTerm("");
  };

  const formatDisplayText = (option: any) => {
    if (!option) return "";

    if (getDisplayText) {
      return getDisplayText(option);
    }

    if (displayFormat) {
      return displayFormat(option);
    }
  };

  const formatSecondaryText = (option: any) => {
    if (getSecondaryText) {
      return getSecondaryText(option);
    }

    return option.mail || option.email || null;
  };

  return (
    <Box position="relative" ref={ref}>
      <InputGroup>
        <Input
          placeholder={
            selectedOption ? formatDisplayText(selectedOption) : placeholder
          }
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => !isDisabled && onToggle()}
          isDisabled={isDisabled}
          cursor={isDisabled ? "not-allowed" : "pointer"}
          readOnly={!isOpen}
          _placeholder={{
            color: placeholderColor,
          }}
          flex={1}
          width="100%"
        />
        <InputRightElement
          style={{
            justifyContent: "flex-end",
            alignItems: "center",
          }}
          w={20}
        >
          {selectedOption && (
            <button
              style={{
                alignItems: "center",
                justifyContent: "center",
                paddingRight: "10px",
              }}
              onClick={handleClear}
            >
              <Icon as={CloseIcon} width={2} height={2} strokeWidth={3} />
            </button>
          )}
          <button
            onClick={onToggle}
            style={{
              alignItems: "center",
              justifyContent: "center",
              paddingRight: "10px",
            }}
          >
            <Icon
              style={{}}
              as={isOpen ? ChevronUpIcon : ChevronDownIcon}
              width={5}
              height={5}
            />
          </button>
        </InputRightElement>
      </InputGroup>

      {isOpen && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          zIndex={1000}
          bg="white"
          border="1px"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="md"
          maxH={maxHeight}
          overflowY="auto"
          mt={1}
        >
          {filteredOptions.length === 0 ? (
            <Box p={3} textAlign="center" color="gray.500">
              {noOptionsText}
            </Box>
          ) : (
            <VStack align="stretch" spacing={0}>
              {filteredOptions.map((option) => {
                const secondaryText = formatSecondaryText(option);
                return (
                  <Box
                    key={option[valueKey]}
                    p={3}
                    cursor="pointer"
                    _hover={{ bg: "gray.50" }}
                    onClick={() => handleSelect(option)}
                    borderBottom="1px"
                    borderColor="gray.100"
                  >
                    <Text fontWeight="medium" fontSize="sm">
                      {formatDisplayText(option)}
                    </Text>
                    {secondaryText && (
                      <Text fontSize="xs" color="gray.500">
                        {secondaryText}
                      </Text>
                    )}
                  </Box>
                );
              })}
            </VStack>
          )}
        </Box>
      )}
    </Box>
  );
};
